import { FormEvent, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { APIError } from "../api";
import useAuthenticator, {
  AuthenticatorResult,
} from "../hooks/useAuthenticator";

type Mode =
  | "idle"
  | "enable"
  | "confirm"
  | "disable"
  | "recovery-codes"
  | "saved-codes";
const buttonClass =
  "rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-bold text-deepBlue disabled:opacity-50";
const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-deepBlue";

export default function AuthenticatorSettings() {
  const { status, loading, statusError, refetch, submit, busy } =
    useAuthenticator();
  const [mode, setMode] = useState<Mode>("idle");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState<AuthenticatorResult | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const close = () => {
    setMode("idle");
    setPassword("");
    setCode("");
    setSetup(null);
    setCodes([]);
    setSaved(false);
    setError("");
  };
  const begin = (next: Mode) => {
    close();
    setMode(next);
  };
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError("");
    try {
      if (mode === "enable") {
        const result = await submit("setup", password);
        if (!result.secret || !result.qr_code)
          throw new Error("Setup could not be loaded. Start again.");
        setSetup(result);
        setMode("confirm");
      } else if (mode === "confirm" || mode === "recovery-codes") {
        const result = await submit(mode, password, code.trim());
        if (!result.recovery_codes?.length)
          throw new Error(
            "The operation may have completed, but the recovery codes could not be loaded. Refresh settings before retrying.",
          );
        setCodes(result.recovery_codes);
        setPassword("");
        setCode("");
        setSetup(null);
        setMode("saved-codes");
      } else if (mode === "disable") {
        await submit("disable", password, code.trim());
        close();
        toast.success("Authenticator disabled.");
      }
    } catch (caught) {
      const validation =
        caught instanceof APIError
          ? Object.values(caught.validationErrors).flat()[0]
          : undefined;
      setError(
        validation ||
          (caught instanceof Error
            ? caught.message
            : "Something went wrong. Please try again."),
      );
      void refetch(); // Recover status when a response was lost after the operation succeeded.
    }
  };
  const copy = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch {
      toast.error("Copy is unavailable. Please save the text manually.");
    }
  };

  return (
    <section
      aria-labelledby="authenticator-title"
      className="flex flex-col gap-3 border-y border-gray-200 py-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="authenticator-title"
          className="text-sm font-bold text-gray-700"
        >
          Authenticator app
        </h2>
        {status && (
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${status.enabled ? "bg-green-50 text-green-800" : "bg-gray-100 text-gray-600"}`}
          >
            {status.enabled ? "Enabled" : "Not enabled"}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600">
        Add a second step to sign-in with Google Authenticator, Microsoft
        Authenticator, Authy or another compatible app.
      </p>
      {loading && (
        <p role="status" className="text-sm text-gray-500">
          Checking your settings…
        </p>
      )}
      {statusError && (
        <div role="alert" className="text-sm text-red-700">
          We couldn’t load your authenticator settings.{" "}
          <button
            type="button"
            className="underline"
            onClick={() => void refetch()}
          >
            Try again
          </button>
        </div>
      )}
      {status &&
        mode === "idle" &&
        (status.enabled ? (
          <>
            <p className="text-sm text-gray-500">
              {status.recovery_codes_remaining} recovery codes remaining. Each
              code can be used once if you lose access to your app.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                className={buttonClass}
                onClick={() => begin("recovery-codes")}
              >
                New recovery codes
              </button>
              <button
                type="button"
                className={buttonClass}
                onClick={() => begin("disable")}
              >
                Disable authenticator
              </button>
            </div>
          </>
        ) : status.enrollment_available ? (
          <button
            type="button"
            className={`${buttonClass} w-fit`}
            onClick={() => begin("enable")}
          >
            Enable authenticator
          </button>
        ) : (
          <p className="text-sm text-gray-500">
            Authenticator setup will be available soon. Your current sign-in
            method is unchanged.
          </p>
        ))}

      <AnimatePresence mode="wait">
        {mode !== "idle" && (
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-xl border border-gray-200 bg-gray-50 p-5"
          >
            {mode === "saved-codes" ? (
              <div className="flex flex-col gap-4">
                <h3 className="font-bold text-deepBlue">
                  Save your recovery codes
                </h3>
                <p className="text-sm text-gray-600">
                  Keep these somewhere private, separate from your phone. They
                  will not be shown again. New codes replace all previous codes.
                </p>
                <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {codes.map((value) => (
                    <li
                      key={value}
                      className="rounded border border-gray-200 bg-white px-3 py-2 font-mono text-sm select-all"
                    >
                      {value}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={`${buttonClass} w-fit`}
                  onClick={() =>
                    void copy(
                      codes.join("\n"),
                      "Recovery codes copied. Store them somewhere safe.",
                    )
                  }
                >
                  Copy recovery codes
                </button>
                <label className="flex items-start gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={saved}
                    onChange={(e) => setSaved(e.target.checked)}
                    className="mt-1 accent-deepBlue"
                  />
                  I have saved my recovery codes somewhere safe.
                </label>
                <button
                  type="button"
                  disabled={!saved}
                  onClick={close}
                  className={`${buttonClass} w-fit`}
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <h3 className="font-bold text-deepBlue">
                  {mode === "enable"
                    ? "Confirm it’s you"
                    : mode === "confirm"
                      ? "Connect your authenticator"
                      : mode === "disable"
                        ? "Disable your authenticator?"
                        : "Replace recovery codes"}
                </h3>
                {mode === "confirm" && setup && (
                  <>
                    <p className="text-sm text-gray-600">
                      Scan this code in your app. Setup expires after 10
                      minutes.
                    </p>
                    <img
                      src={setup.qr_code}
                      alt="Scan this QR code in your authenticator app"
                      className="h-56 w-56 max-w-full self-center rounded-lg bg-white p-2"
                    />
                    <p className="text-sm text-gray-600">
                      Can’t scan it? Enter this setup key manually:
                    </p>
                    <code className="break-all rounded-lg border border-gray-200 bg-white p-3 text-sm select-all">
                      {setup.secret}
                    </code>
                    <button
                      type="button"
                      className={`${buttonClass} w-fit`}
                      onClick={() =>
                        void copy(setup.secret!, "Setup key copied.")
                      }
                    >
                      Copy setup key
                    </button>
                  </>
                )}
                {mode !== "confirm" && (
                  <>
                    <p className="text-sm text-gray-600">
                      {mode === "enable"
                        ? "Enter your current password. Your existing sign-in method stays active until you confirm setup."
                        : mode === "disable"
                          ? "Your password and a current authenticator or unused recovery code are required. Your previous sign-in method will be restored."
                          : "Your previous recovery codes will stop working. Enter your password and a current authenticator or unused recovery code."}
                    </p>
                    <label
                      htmlFor="authenticator-password"
                      className="text-sm font-medium text-gray-700"
                    >
                      Current password
                    </label>
                    <input
                      id="authenticator-password"
                      type="password"
                      autoComplete="current-password"
                      required
                      maxLength={255}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={inputClass}
                    />
                  </>
                )}
                {mode !== "enable" && (
                  <>
                    <label
                      htmlFor="authenticator-code"
                      className="text-sm font-medium text-gray-700"
                    >
                      {mode === "confirm"
                        ? "6-digit authenticator code"
                        : "Authenticator or recovery code"}
                    </label>
                    <input
                      id="authenticator-code"
                      type="text"
                      autoComplete="one-time-code"
                      inputMode={mode === "confirm" ? "numeric" : "text"}
                      required
                      maxLength={mode === "confirm" ? 6 : 23}
                      pattern={
                        mode === "confirm"
                          ? "[0-9]{6}"
                          : "([0-9]{6}|[a-fA-F0-9]{20}|[a-fA-F0-9]{5}(-[a-fA-F0-9]{5}){3})"
                      }
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      className={inputClass}
                    />
                    <p className="text-xs text-gray-500">
                      Use a fresh code for each action. Email codes cannot
                      replace your authenticator.
                    </p>
                  </>
                )}
                {error && (
                  <p role="alert" className="text-sm text-red-700">
                    {error}
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={busy}
                    className="rounded-lg bg-deepBlue px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
                  >
                    {busy
                      ? "Please wait…"
                      : mode === "enable"
                        ? "Continue"
                        : mode === "confirm"
                          ? "Confirm and enable"
                          : mode === "disable"
                            ? "Disable authenticator"
                            : "Generate new codes"}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={close}
                    className={buttonClass}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
